// SpecialContributionCreate.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box, Card, CardContent, Typography, Grid, TextField, Button, Chip,
  Divider, InputAdornment, Checkbox, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Tooltip, Switch, FormControlLabel
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import Swal from "sweetalert2";
import { useAuth } from "../contexts/AuthContext";
import { fetchMembers } from "../services/memberService";
import { fetchPrograms, createProgram } from "../services/specialContributionService";
import LoadingSpinner from "../components/LoadingSpinner";
import { useTranslation } from "react-i18next";
import formatCurrency from "../shared/formatCurrency";

const brand = "#800b33";
const brandHover = "#6c0a2b";


// const currencyFmt = (v) => new Intl.NumberFormat("en-TZ").format(v);
const ACTIVE_PIPELINE_STATUSES = [
  "ACTIVE",
  "PENDING_CHAIR_APPROVAL",
  "PENDING_SECRETARY_APPROVAL",
  "PENDING_ACCOUNTANT_APPROVAL",
];

// ---------------- Component ----------------
export default function SpecialContribution() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "sw" ? "sw-TZ" : "en-TZ";
  const { user } = useAuth();
  const leaderName = user?.member_full_name || user?.full_name || "—";

  // form state
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [targetPerMember, setTargetPerMember] = useState(30000);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [query, setQuery] = useState("");
  const [selectAll, setSelectAll] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [members, setMembers] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadInitialData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [membersData, programsData] = await Promise.all([fetchMembers(), fetchPrograms()]);
      setMembers(membersData);
      setPrograms(programsData);
      setSelectedIds(membersData.filter((m) => m.is_active).map((m) => m.id));
    } catch (err) {
      setLoadError(err.message);
      setLoadError(err.message);
      Swal.fire({ icon: "error", title: t("Failed to load data"), text: err.message, confirmButtonColor: brand });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const refreshPrograms = useCallback(async () => {
    try {
      const data = await fetchPrograms();
      setPrograms(data);
    } catch (err) {
      console.error("Failed to refresh programs:", err);
    }
  }, []);

  // derived: filtered members
  const filteredMembers = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = members;
    if (!q) return base;
    return base.filter(m =>
      [m.user?.full_name, m.user?.phone_number, m.role]
        .filter(Boolean)
        .some(x => x.toLowerCase().includes(q))
    );
  }, [query, members]);

  // when toggling "All members"
  useEffect(() => {
    if (selectAll) {
      setSelectedIds(members.filter(m => m.is_active).map(m => m.id));
    }
  }, [selectAll, members]);

  const toggleOne = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // computed summary
  const selectedActiveCount = useMemo(
    () =>
      members.filter((m) => m.is_active && selectedIds.includes(m.id)).length,
    [members, selectedIds]
  );
  const totalTarget = (Number(targetPerMember) || 0) * selectedActiveCount;

  // validations
  const validate = () => {
    // initiator must be active
    if (!user?.member_is_active) {
      Swal.fire({ icon: "error", title: t("Initiator inactive"), text: t("Only active members can initiate a program."), confirmButtonColor: brand });
      return false;
    }
    // title empty
    if (!title.trim()) {
      Swal.fire({ icon: "warning", title: t("Missing Title"), text: t("Please enter a program title."), confirmButtonColor: brand });
      return false;
    }
    // duplicate active titles
    const normalized = title.trim().toLowerCase();
    const dup = programs.some(
      (p) =>
        ACTIVE_PIPELINE_STATUSES.includes(p.status) &&
        p.title.toLowerCase() === normalized
    );
    if (dup) {
      Swal.fire({ icon: "error", title: t("Duplicate Active Program"), text: t("Another active program with the same title exists."), confirmButtonColor: brand });
      return false;
    }
    if (!targetPerMember || Number(targetPerMember) <= 0) {
      Swal.fire({ icon: "warning", title: t("Invalid Target"), text: t("Target per member must be greater than zero."), confirmButtonColor: brand });
      return false;
    }
    if (!start || !end) {
      Swal.fire({ icon: "warning", title: t("Missing Dates"), text: t("Please select start and end dates."), confirmButtonColor: brand });
      return false;
    }
    if (new Date(end) <= new Date(start)) {
      Swal.fire({ icon: "warning", title: t("Invalid Timeframe"), text: t("End date must be after start date."), confirmButtonColor: brand });
      return false;
    }
    if (selectedActiveCount === 0) {
      Swal.fire({ icon: "warning", title: t("No Participants"), text: t("Select at least one active participant."), confirmButtonColor: brand });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    // payload example (swap with API)
    const payload = {
      title: title.trim(),
      description: desc.trim(),
      target_per_member: Number(targetPerMember),
      start_date: start,
      end_date: end,
      participant_ids: selectedIds,
    };

    try {
      await createProgram(payload);
      Swal.fire({
        icon: "success",
        title: t("Program Created"),
        html: `
          <div style="text-align:left">
            <strong>${t("Title *")}:</strong> ${payload.title}<br/>
            <strong>${t("Leader:")}</strong> ${leaderName}<br/>
            <strong>${t("Selected (Active)")}:</strong> ${selectedActiveCount}<br/>
            <strong>${t("Target / Member")}:</strong> ${formatCurrency(payload.target_per_member, locale)} TZS<br/>
            <strong>${t("Total Target")}:</strong> ${formatCurrency(totalTarget, locale)} TZS<br/>
            <hr style="margin:8px 0"/>
            <small>${t("Program requires approvals (Chair → Katibu → Muhasibu).")}<br/>${t("Invoices will be generated per member on start.")}</small>
          </div>
        `,
        confirmButtonColor: brand,
      });
      await refreshPrograms();
      setTitle("");
      setDesc("");
      setTargetPerMember(30000);
      setStart("");
      setEnd("");
      setSelectAll(true);
    } catch (err) {
      Swal.fire({ icon: "error", title: t("Creation Failed"), text: err.message, confirmButtonColor: brand });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, textAlign: "center" }}>
        <Typography color="error" sx={{ mb: 2 }}>
          {loadError}
        </Typography>
        <Button variant="contained" onClick={loadInitialData} sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}>
          {t("Retry")}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
          {t("Start Special Contribution Program")}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          {t("Define program details and select participants (subset or all members).")}
        </Typography>
      </Box>

      {/* Program details */}
      <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                label={t("Title *")}
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("e.g., Mama Neema Delivery Support")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("Target per member (TZS) *")}
                type="number"
                fullWidth
                value={targetPerMember}
                onChange={(e) => setTargetPerMember(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">TZS</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("Description")}
                fullWidth
                multiline
                minRows={2}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder={t("Short reason or context for this program...")}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label={t("Start Date *")}
                type="date"
                fullWidth
                value={start}
                onChange={(e) => setStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={t("End Date *")}
                type="date"
                fullWidth
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={3} display="flex" alignItems="center">
              <Chip
                icon={<PersonOutlinedIcon />}
                label={`${t("Leader:")} ${leaderName}`}
                sx={{ bgcolor: `${brand}14`, color: brand, fontWeight: 600 }}
              />
            </Grid>

            <Grid item xs={12} md={3} display="flex" alignItems="center" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={selectAll}
                    onChange={(e) => setSelectAll(e.target.checked)}
                  />
                }
                label={t("All Active Members")}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Summary strip */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography fontWeight={700} variant="body2" color="text.secondary">
                  {t("Selected (Active)")}
                </Typography>
                <Typography variant="h6" fontWeight={900}>{selectedActiveCount}</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography fontWeight={700} variant="body2" color="text.secondary">
                  {t("Target / Member")}
                </Typography>
                <Typography variant="h6" fontWeight={900}>{formatCurrency(Number(targetPerMember) || 0, locale)} TZS</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography fontWeight={700} variant="body2" color="text.secondary">
                  {t("Total Target")}
                </Typography>
                <Typography variant="h6" fontWeight={900}>{formatCurrency(totalTarget, locale)} TZS</Typography>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Participant selection */}
      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="h6" fontWeight={800}>
              {t("Select Participants")}
            </Typography>
            <Tooltip title={t("Only active members are eligible to be charged. Inactive appear for transparency.")}>
              <InfoOutlinedIcon sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Box>

          {!selectAll && (
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                placeholder={t("Search by name, phone, or role...")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchIcon /></InputAdornment>
                  ),
                }}
              />
            </Box>
          )}

          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        selectAll
                          ? true
                          : filteredMembers.filter(m => m.is_active).every(m => selectedIds.includes(m.id))
                      }
                      indeterminate={
                        !selectAll &&
                        filteredMembers.some(m => m.is_active && selectedIds.includes(m.id)) &&
                        !filteredMembers.filter(m => m.is_active).every(m => selectedIds.includes(m.id))
                      }
                      onChange={() => {
                        if (selectAll) {
                          // nothing (global switch manages)
                          return;
                        }
                        const activeFiltered = filteredMembers.filter(m => m.is_active).map(m => m.id);
                        const allSelected = activeFiltered.every(id => selectedIds.includes(id));
                        if (allSelected) {
                          setSelectedIds(prev => prev.filter(id => !activeFiltered.includes(id)));
                        } else {
                          setSelectedIds(prev => Array.from(new Set([...prev, ...activeFiltered])));
                        }
                      }}
                      disabled={selectAll}
                    />
                  </TableCell>
                  <TableCell>{t("Name")}</TableCell>
                  <TableCell>{t("Phone")}</TableCell>
                  <TableCell>{t("Role")}</TableCell>
                  <TableCell>{t("Status")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(selectAll ? members : filteredMembers).map((m) => {
                  const isChecked = selectedIds.includes(m.id);
                  const memberName = m.user?.full_name || m.name || "—";
                  const memberPhone = m.user?.phone_number || "—";
                  return (
                    <TableRow key={m.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isChecked}
                          onChange={() => toggleOne(m.id)}
                          disabled={selectAll}
                        />
                      </TableCell>
                      <TableCell>{memberName}</TableCell>
                      <TableCell>{memberPhone}</TableCell>
                      <TableCell>
                        <Chip label={m.role} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={m.is_active ? t("Active") : t("Inactive")}
                          size="small"
                          color={m.is_active ? "success" : "default"}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {(!selectAll && filteredMembers.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      {t("No members match your search.")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="body2" color="text.secondary">
                {t("On Start Date, the system will generate a unique invoice with a reference for each selected member and send reminders until the End Date.")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} display="flex" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
              <Button
                variant="outlined"
                startIcon={<PlaylistAddCheckOutlinedIcon />}
                sx={{ mr: 1, borderColor: brand, color: brand, "&:hover": { borderColor: brandHover, color: brandHover } }}
                onClick={() => {
                  Swal.fire({
                    icon: "info",
                    title: t("Validation Check"),
                    html: `
                      <div style="text-align:left">
                        <strong>${t("Title *")}:</strong> ${title || "(empty)"}<br/>
                        <strong>${t("Dates")}:</strong> ${start || "..."} → ${end || "..."}<br/>
                        <strong>${t("Selected (Active)")}:</strong> ${selectedActiveCount}<br/>
                        <strong>${t("Total Target")}:</strong> ${formatCurrency(totalTarget, locale)} TZS
                      </div>
                    `,
                    confirmButtonColor: brand,
                  });
                }}
              >
                {t("Validate")}
              </Button>
              <Button
                variant="contained"
                startIcon={<GroupAddOutlinedIcon />}
                onClick={handleSubmit}
                disabled={isSubmitting}
                sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
              >
                {isSubmitting ? t("Submitting...") : t("Create Program")}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}
